import * as React from 'react';
import { CheckCircle2, FileWarning, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * View / replace a registration's Apple domain-association file — the exact
 * bytes we serve at `https://<domain>/.well-known/apple-developer-merchantid-domain-association`
 * for every domain attached to the registration. Data access is injected so
 * the merchant page and the account-manager tab share this UI.
 */
export function AssociationFileCard({
  hasFile,
  domains,
  busy,
  currentContent,
  loadingContent,
  onOpenEditor,
  onSave,
}: {
  hasFile: boolean;
  /** Attached domains, used to show where the file is served. */
  domains: string[];
  busy: boolean;
  /** Stored content (fetched lazily once the editor opens). */
  currentContent: string | null | undefined;
  loadingContent: boolean;
  /** Called when the editor opens — surface should enable its content query. */
  onOpenEditor: () => void;
  onSave: (content: string) => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string | null>(null);

  // Seed the editor with the stored content once it arrives; `draft` is only
  // reset when the dialog (re)opens.
  const value = draft ?? currentContent ?? '';

  const handleOpen = () => {
    setDraft(null);
    onOpenEditor();
    setOpen(true);
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setDraft((await file.text()).trim());
  };

  const handleSave = async () => {
    if (!value.trim()) return;
    await onSave(value.trim());
    setOpen(false);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Domain-association file</h4>
        <Button variant="outline" size="sm" onClick={handleOpen}>
          <Pencil className="mr-2 h-4 w-4" />
          {hasFile ? 'View / replace' : 'Add file'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        {hasFile ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Configured
          </Badge>
        ) : (
          <Badge variant="destructive">
            <FileWarning className="mr-1 h-3.5 w-3.5" />
            Missing — domain verification will fail
          </Badge>
        )}
        <span className="text-muted-foreground text-xs">
          Download it from the Apple Developer portal (Merchant ID → domain verification).
        </span>
      </div>

      {domains.length > 0 && (
        <div className="text-muted-foreground space-y-1 text-xs">
          <p>Served automatically at (Apple checks the .txt path for BYOK):</p>
          {domains.map((d) => (
            <code key={d} className="block break-all">
              https://{d}/.well-known/apple-developer-merchantid-domain-association.txt
            </code>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{hasFile ? 'Domain-association file' : 'Add domain-association file'}</DialogTitle>
            <DialogDescription>
              The exact content served at every attached domain&apos;s{' '}
              <code>/.well-known/apple-developer-merchantid-domain-association</code> path. Replacing it takes
              effect immediately — re-verify with Apple / re-probe afterwards.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Input type="file" onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />
            <Textarea
              value={loadingContent && draft === null ? 'Loading…' : value}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="…or paste the association file contents"
              className="h-40 font-mono text-xs"
              disabled={loadingContent && draft === null}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={busy || !value.trim()}>
              {busy ? 'Saving…' : 'Save file'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
